/**
 * OpenClawInstaller — Instala OpenClaw via npm na máquina remota
 * 
 * Fluxo:
 * 1. Botão "Instalar OpenClaw" ao lado do Terminal
 * 2. Modal de confirmação
 * 3. Envia comandos sequencialmente via terminal exec
 * 4. Card com progresso em tempo real (steps + %)
 * 5. Erros e prompts aparecem de forma amigável
 */
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { api } from '../api/client';
import { T } from '../styles/tokens';
import { Button } from './ui';

// ──────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────

interface InstallStep {
  id: string;
  label: string;
  command: string;
  status: 'waiting' | 'running' | 'done' | 'error' | 'skipped';
  output: string;
  exitCode?: number;
  cmdId?: string;
  /** If true, check output before proceeding (e.g. node version check) */
  validator?: (output: string, exitCode: number) => { ok: boolean; skip?: boolean; message?: string };
}

interface Props {
  runnerId: string;
  isOnline: boolean;
  runnerOs: string;
  openclawPath?: string;
  onClose: () => void;
  onBusyChange?: (busy: boolean) => void;
}

// ──────────────────────────────────────────────
// Install Steps Definition
// ──────────────────────────────────────────────

// Shell prefix per OS — set PATH directly (NO source .zshrc — it can hang!)
function getPathPrefix(runnerOs: string): string {
  if (runnerOs === 'macos') {
    return 'export PATH="/opt/homebrew/bin:/opt/homebrew/sbin:/usr/local/bin:/usr/local/sbin:$HOME/.nvm/versions/node/*/bin:$HOME/.volta/bin:$PATH"; ';
  }
  // Linux / Windows (WSL)
  return 'export PATH="/usr/local/bin:/usr/bin:$HOME/.nvm/versions/node/*/bin:$HOME/.volta/bin:$HOME/.bun/bin:$PATH"; ';
}

function getNodeInstallHint(runnerOs: string): string {
  if (runnerOs === 'macos') return 'No macOS: brew install node';
  if (runnerOs === 'windows') return 'No Windows: https://nodejs.org/en/download';
  return 'No Linux: curl -fsSL https://deb.nodesource.com/setup_22.x | sudo -E bash - && sudo apt-get install -y nodejs';
}

function getPermissionHint(runnerOs: string): string {
  if (runnerOs === 'macos') return 'Permissão negada. Rode no Terminal:\n\nsudo chown -R $(whoami) /usr/local/lib/node_modules\n\nDepois tente novamente.';
  return 'Permissão negada. Rode com sudo:\n\nsudo npm install -g openclaw\n\nOu corrija permissões: sudo chown -R $(whoami) $(npm config get prefix)/lib/node_modules';
}

function getOpenClawPath(runnerOs: string): string {
  if (runnerOs === 'macos') return '/opt/homebrew/bin/openclaw';
  return '/usr/local/bin/openclaw ou /usr/bin/openclaw';
}

function createSteps(runnerOs: string): InstallStep[] {
  const P = getPathPrefix(runnerOs);
  const nodeHint = getNodeInstallHint(runnerOs);

  return [
    {
      id: 'setup-path',
      label: 'Detectando ambiente',
      command: P + 'echo "OK" && uname -ms 2>&1',
      status: 'waiting',
      output: '',
      validator: (_output, exitCode) => {
        if (exitCode !== 0) {
          return { ok: false, message: 'Não foi possível detectar o ambiente do sistema.' };
        }
        return { ok: true };
      },
    },
    {
      id: 'check-node',
      label: 'Verificando Node.js',
      command: P + 'node --version 2>&1',
      status: 'waiting',
      output: '',
      validator: (output, exitCode) => {
        if (exitCode !== 0 || !output.trim().match(/v\d+/)) {
          return { ok: false, message: `Node.js não encontrado. Instale Node.js ≥ 18 antes de continuar.\n\n${nodeHint}` };
        }
        const versionLine = output.split('\n').find(l => l.trim().startsWith('v')) || '';
        const ver = parseInt(versionLine.trim().replace('v', '').split('.')[0] || '0', 10);
        if (ver < 18) {
          return { ok: false, message: `Node.js ${versionLine.trim()} é muito antigo. Necessário ≥ v18.\n\n${nodeHint}` };
        }
        return { ok: true, message: `Node.js ${versionLine.trim()} ` };
      },
    },
    {
      id: 'check-npm',
      label: 'Verificando npm',
      command: P + 'npm --version 2>&1',
      status: 'waiting',
      output: '',
      validator: (output, exitCode) => {
        if (exitCode !== 0) {
          return { ok: false, message: 'npm não encontrado.' };
        }
        const ver = output.split('\n').find(l => l.trim().match(/^\d+/)) || output.trim();
        return { ok: true, message: `npm ${ver.trim()} ` };
      },
    },
    {
      id: 'check-existing',
      label: 'Verificando instalação existente',
      command: P + '(which openclaw && openclaw --version) 2>/dev/null || echo "NOT_INSTALLED"',
      status: 'waiting',
      output: '',
      validator: (output) => {
        if (output.includes('NOT_INSTALLED')) {
          return { ok: true, message: 'OpenClaw não encontrado — será instalado.' };
        }
        return { ok: true, message: `OpenClaw já instalado: ${output.trim()}. Será atualizado.` };
      },
    },
    {
      id: 'install-openclaw',
      label: 'Instalando OpenClaw via npm',
      command: P + 'npm install -g openclaw 2>&1',
      status: 'waiting',
      output: '',
      validator: (output, exitCode) => {
        if (exitCode !== 0) {
          if (output.includes('EACCES') || output.includes('permission denied')) {
            return { ok: false, message: getPermissionHint(runnerOs) };
          }
          if (output.includes('ENOENT') || output.includes('not found')) {
            return { ok: false, message: 'npm não conseguiu resolver o pacote. Verifique sua conexão com internet.' };
          }
          return { ok: false, message: `Erro na instalação:\n${output.slice(-300)}` };
        }
        return { ok: true };
      },
    },
    {
      id: 'verify-install',
      label: 'Verificando instalação',
      command: P + 'openclaw --version 2>&1 && which openclaw 2>&1',
      status: 'waiting',
      output: '',
      validator: (output, exitCode) => {
        if (exitCode !== 0) {
          return { ok: false, message: 'OpenClaw instalado mas não encontrado no PATH. Tente abrir um novo terminal.' };
        }
        return { ok: true, message: `OpenClaw ${output.split('\n')[0]?.trim()} ` };
      },
    },
    {
      id: 'setup-openclaw',
      label: 'Configurando OpenClaw',
      command: P + 'mkdir -p ~/.openclaw && openclaw status 2>&1 || echo "NEEDS_SETUP"',
      status: 'waiting',
      output: '',
      validator: (output) => {
        if (output.includes('NEEDS_SETUP') || output.includes('not configured')) {
          return { ok: true, message: 'OpenClaw precisa de configuração inicial. Use: openclaw setup' };
        }
        return { ok: true };
      },
    },
    {
      id: 'start-gateway',
      label: 'Iniciando OpenClaw Gateway',
      command: P + '(openclaw gateway status 2>&1 | grep -qi "running" && echo "ALREADY_RUNNING") || (nohup openclaw gateway start > ~/.openclaw/gateway.log 2>&1 & sleep 3 && openclaw gateway status 2>&1)',
      status: 'waiting',
      output: '',
      validator: (output, exitCode) => {
        if (output.includes('ALREADY_RUNNING')) {
 return { ok: true, message: 'OpenClaw Gateway já estava rodando!' };
        }
        if (output.includes('running') || output.includes('online') || output.includes('started') || exitCode === 0) {
 return { ok: true, message: 'OpenClaw Gateway iniciado com sucesso!' };
        }
        return { ok: true, message: 'Gateway pode precisar de configuração manual. Rode: openclaw setup' };
      },
    },
  ];
}

// ──────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────

const POLL_MS = 800;

export function OpenClawInstaller({ runnerId, isOnline, runnerOs, onClose, onBusyChange }: Props) {
  const [phase, setPhase] = useState<'confirm' | 'installing' | 'done' | 'error'>('confirm');
  const [steps, setSteps] = useState<InstallStep[]>(() => createSteps(runnerOs));
  const [currentStep, setCurrentStep] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const abortRef = useRef(false);
  const pollRef = useRef<any>(null);

  const totalSteps = steps.length;
  const doneCount = steps.filter(s => s.status === 'done' || s.status === 'skipped').length;
  const progress = phase === 'done' ? 100 : Math.round((doneCount / totalSteps) * 100);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // ── Send command and poll for result ──
  const execCommand = useCallback(async (command: string): Promise<{ output: string; exitCode: number; error?: string }> => {
    try {
      const res = await api.post(`/runner-v3/${runnerId}/terminal/exec`, { command }) as any;
      if (!res.success || !res.data?.id) {
        return { output: '', exitCode: -1, error: 'Falha ao enviar comando' };
      }
      const cmdId = res.data.id;

      // Poll for completion
      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 375; // 375 * 800ms = ~5min timeout
        let lastOutput = '';
        let stableCount = 0;
        let polling = false;

        pollRef.current = setInterval(async () => {
          if (polling) return; // prevent overlapping async calls
          polling = true;
          attempts++;

          if (abortRef.current) {
            clearInterval(pollRef.current);
            resolve({ output: '', exitCode: -1, error: 'Cancelado' });
            polling = false;
            return;
          }

          try {
            const cmd = await api.get(`/runner-v3/${runnerId}/terminal/output/${cmdId}`) as any;
            if (cmd) {
              if (cmd.status === 'completed' || cmd.status === 'error') {
                clearInterval(pollRef.current);
                resolve({
                  output: cmd.output || '',
                  exitCode: cmd.exit_code ?? (cmd.status === 'error' ? 1 : 0),
                });
                polling = false;
                return;
              }
              // Still running — update output in real-time
              if (cmd.output) {
                setSteps(prev => prev.map((s, i) =>
                  i === currentStep ? { ...s, output: cmd.output } : s
                ));
                // Detect stale "running" — output stable for 15 polls (~12s) → treat as done
                if (cmd.output === lastOutput) {
                  stableCount++;
                  if (stableCount >= 15 && cmd.output.length > 0) {
                    clearInterval(pollRef.current);
                    resolve({ output: cmd.output, exitCode: 0 });
                    polling = false;
                    return;
                  }
                } else {
                  stableCount = 0;
                  lastOutput = cmd.output;
                }
              }
            }
          } catch {}

          if (attempts >= maxAttempts) {
            clearInterval(pollRef.current);
            resolve({ output: lastOutput || '', exitCode: -1, error: 'Timeout — comando demorou mais de 5 min' });
          }
          polling = false;
        }, POLL_MS);
      });
    } catch (e: any) {
      return { output: '', exitCode: -1, error: e?.message || 'Erro de conexão' };
    }
  }, [runnerId, currentStep]);

  // ── Run all steps sequentially ──
  const startInstall = useCallback(async () => {
    setPhase('installing');
    onBusyChange?.(true);
    abortRef.current = false;

    const stepsLocal = createSteps(runnerOs);
    setSteps(stepsLocal);

    for (let i = 0; i < stepsLocal.length; i++) {
      if (abortRef.current) break;
      const step = stepsLocal[i]!;
      setCurrentStep(i);

      // Mark as running
      step.status = 'running';
      setSteps([...stepsLocal]);

      const result = await execCommand(step.command);

      if (result.error) {
        step.status = 'error';
        step.output = result.error;
        setSteps([...stepsLocal]);
        setErrorMessage(result.error);
        setPhase('error');
        return;
      }

      step.output = result.output;
      step.exitCode = result.exitCode;

      // Run validator
      if (step.validator) {
        const validation = step.validator(result.output, result.exitCode);
        if (!validation.ok) {
          step.status = 'error';
          setSteps([...stepsLocal]);
          setErrorMessage(validation.message || 'Falha na validação');
          setPhase('error');
          return;
        }
        if (validation.skip) {
          step.status = 'skipped';
          if (validation.message) setInfoMessage(validation.message);
          setSteps([...stepsLocal]);
          continue;
        }
        if (validation.message) setInfoMessage(validation.message);
      }

      step.status = 'done';
      setSteps([...stepsLocal]);
    }

    if (!abortRef.current) {
      setPhase('done');
    }
    onBusyChange?.(false);
  }, [execCommand]);

  const handleCancel = () => {
    abortRef.current = true;
    if (pollRef.current) clearInterval(pollRef.current);
    onClose();
  };

  const handleRetry = () => {
    setErrorMessage('');
    setInfoMessage('');
    startInstall();
  };

  // ── Confirmation screen ──
  if (phase === 'confirm') {
    return (
      <div style={{
        marginBottom: 20, borderRadius: 14, overflow: 'hidden',
        border: `1px solid ${T.accentBgStrong}`, background: 'var(--bg-card-solid)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          background: T.accentBg,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <span style={{ fontSize: 16 }}></span>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Instalar OpenClaw</span>
          </div>
          <Button variant="ghost" onClick={handleCancel} style={{
            background: 'none', border: 'none', color: 'var(--text-dim)',
            fontSize: 16, cursor: 'pointer', padding: '2px 4px',
          }}></Button>
        </div>

        <div style={{ padding: '16px' }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.6 }}>
            Isso vai instalar o <strong style={{ color: 'var(--text)' }}>OpenClaw</strong> via npm nesta máquina e iniciar o Gateway automaticamente.
          </p>

          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
            {createSteps(runnerOs).map((s, i) => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{
                  width: 18, height: 18, borderRadius: '50%', background: T.accentBg,
                  color: 'var(--secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 700, flexShrink: 0,
                }}>{i + 1}</span>
                <span>{s.label}</span>
              </div>
            ))}
          </div>

          <div style={{
            padding: '8px 12px', borderRadius: 8, marginBottom: 16,
            background: T.warningBg, border: `1px solid ${T.warningBg}`,
            fontSize: 11, color: 'var(--accent)',
          }}>
 <strong>Requisitos:</strong>Node.js ≥ 18 e npm instalados.
            {runnerOs === 'macos' ? (
              <> No Mac: <code style={{ background: T.bgHover, padding: '1px 4px', borderRadius: 3 }}>brew install node</code></>
            ) : (
              <> No Linux: <code style={{ background: T.bgHover, padding: '1px 4px', borderRadius: 3 }}>apt install nodejs npm</code> ou <code style={{ background: T.bgHover, padding: '1px 4px', borderRadius: 3 }}>nvm install 22</code></>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={handleCancel} style={btnSecondaryStyle}>Cancelar</Button>
            <Button variant="ghost" onClick={startInstall} disabled={!isOnline} style={{
              ...btnPrimaryStyle,
              opacity: isOnline ? 1 : 0.5,
              cursor: isOnline ? 'pointer' : 'not-allowed',
            }}>
 {isOnline ? 'Instalar Agora' : 'Máquina Offline'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Installing / Done / Error — Progress Card ──
  return (
    <div style={{
      marginBottom: 20, borderRadius: 14, overflow: 'hidden',
      border: phase === 'done' ? '1px solid rgba(46,204,113,0.3)'
        : phase === 'error' ? '1px solid rgba(248,81,73,0.3)'
        : `1px solid ${T.accentBgStrong}`,
      background: 'var(--bg-card-solid)',
      animation: 'slideDown 0.25s ease',
    }}>
      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px',
        background: phase === 'done' ? T.successBg
          : phase === 'error' ? 'rgba(248,81,73,0.08)'
          : T.accentBg,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <span style={{ fontSize: 16 }}></span>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
            {phase === 'done' ? 'OpenClaw Instalado!' : phase === 'error' ? 'Erro na Instalação' : 'Instalando OpenClaw...'}
          </span>
          {phase === 'installing' && (
            <span style={{
              fontSize: 10, padding: '2px 8px', borderRadius: 10,
              background: T.accentBg, color: 'var(--secondary)',
              fontWeight: 600, animation: 'pulse 1.5s infinite',
            }}>EM PROGRESSO</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Progress % */}
          <span style={{
            fontSize: 13, fontWeight: 700, fontFamily: 'monospace',
            color: phase === 'error' ? 'var(--danger)' : phase === 'done' ? 'var(--success)' : 'var(--secondary)',
          }}>
            {progress}%
          </span>
          <Button variant="ghost" onClick={handleCancel} style={{
            background: 'none', border: 'none', color: 'var(--text-dim)',
            fontSize: 16, cursor: 'pointer', padding: '2px 4px', lineHeight: 1,
          }}></Button>
        </div>
      </div>

      {/* Progress Bar (thin) */}
      <div style={{ width: '100%', height: 3, background: T.bgCard }}>
        <div style={{
          width: `${progress}%`, height: '100%',
          background: phase === 'error' ? 'var(--danger)'
            : phase === 'done' ? 'var(--success)'
            : 'linear-gradient(90deg, var(--secondary), var(--secondary))',
          transition: 'width 0.4s ease',
        }} />
      </div>

      {/* Steps */}
      <div style={{
        padding: '12px 16px',
        fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
        maxHeight: 400, overflowY: 'auto',
      }}>
        {steps.map((step, i) => (
          <div key={step.id} style={{ marginBottom: i < steps.length - 1 ? 12 : 0 }}>
            {/* Step header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
              <StepIcon status={step.status} />
              <span style={{
                fontSize: 12, fontWeight: 600,
                color: step.status === 'running' ? 'var(--primary)'
                  : step.status === 'done' ? 'var(--success)'
                  : step.status === 'error' ? 'var(--danger)'
                  : step.status === 'skipped' ? 'var(--text-muted)'
                  : 'var(--text-muted)',
              }}>
                {step.label}
              </span>
              {step.status === 'running' && (
                <span style={{
                  fontSize: 9, padding: '1px 6px', borderRadius: 8,
                  background: T.primaryBg, color: 'var(--primary)',
                  fontWeight: 600, animation: 'pulse 1.5s infinite',
                }}>EXECUTANDO</span>
              )}
              {step.status === 'done' && step.exitCode !== undefined && (
                <span style={{ fontSize: 10, color: 'var(--success)' }}>exit {step.exitCode}</span>
              )}
            </div>

            {/* Command (condensed — hide the long PATH_PREFIX) */}
            {(step.status === 'running' || step.status === 'done' || step.status === 'error') && (
              <div style={{
                fontSize: 10, color: 'var(--text-muted)', marginLeft: 28, marginBottom: 3,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}>
                <span style={{ color: 'var(--success)' }}>$</span> {step.command.replace(/^export PATH.*?;\s*/g, '').replace(/^eval.*?;\s*/g, '').replace(/^source.*?;\s*/g, '').trim() || step.command}
              </div>
            )}

            {/* Output */}
            {step.output && (step.status === 'running' || step.status === 'done' || step.status === 'error') && (
              <div style={{
                marginLeft: 28, fontSize: 10, lineHeight: 1.4,
                color: step.status === 'error' ? 'var(--danger)' : 'var(--text-muted)',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                maxHeight: 100, overflowY: 'auto',
                padding: '4px 8px', borderRadius: 4,
                background: T.bgSubtle,
                borderLeft: `2px solid ${step.status === 'error' ? 'var(--danger)40' : T.bgCard}`,
              }}>
                {step.output.slice(-1500)}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Info / Error messages */}
      {(infoMessage || errorMessage) && (
        <div style={{ padding: '0 16px 12px' }}>
          {infoMessage && (
            <div style={{
              padding: '8px 12px', borderRadius: 8, marginBottom: 8,
              background: T.successBg, border: '1px solid rgba(46,204,113,0.15)',
              fontSize: 12, color: 'var(--success)',
            }}>
              {infoMessage}
            </div>
          )}
          {errorMessage && (
            <div style={{
              padding: '10px 12px', borderRadius: 8,
              background: T.dangerBg, border: '1px solid rgba(248,81,73,0.15)',
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--danger)', marginBottom: 4 }}>
 {errorMessage}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                Tente resolver via Terminal ou clique em "Tentar Novamente".
              </div>
            </div>
          )}
        </div>
      )}

      {/* Success help */}
      {phase === 'done' && (
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{
            padding: '10px 14px', borderRadius: 8,
            background: T.successBg, border: '1px solid rgba(46,204,113,0.15)',
            fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6,
          }}>
 <span style={{ color: 'var(--success)', fontWeight: 600 }}>OpenClaw está instalado!</span><br/>
            <span style={{ color: 'var(--text-muted)' }}>
              Localização: <code style={{ color: 'var(--primary)' }}>{getOpenClawPath(runnerOs)}</code><br/>
              Se precisar configurar: <code style={{ color: 'var(--primary)' }}>openclaw setup</code><br/>
              Ver status: <code style={{ color: 'var(--primary)' }}>openclaw status</code>
            </span>
          </div>
        </div>
      )}

      {/* Footer actions */}
      <div style={{
        display: 'flex', gap: 8, justifyContent: 'flex-end',
        padding: '8px 16px 12px',
        borderTop: '1px solid rgba(255,255,255,0.04)',
      }}>
        {phase === 'installing' && (
          <Button variant="ghost" onClick={handleCancel} style={btnDangerStyle}>Cancelar</Button>
        )}
        {phase === 'error' && (
          <>
            <Button variant="ghost" onClick={handleCancel} style={btnSecondaryStyle}>Fechar</Button>
 <Button variant="ghost" onClick={handleRetry} style={btnPrimaryStyle}>Tentar Novamente</Button>
          </>
        )}
        {phase === 'done' && (
 <Button variant="ghost" onClick={handleCancel} style={btnSuccessStyle}>Concluído</Button>
        )}
      </div>

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideDown { from { opacity: 0; max-height: 0; } to { opacity: 1; max-height: 600px; } }
      `}</style>
    </div>
  );
}

// ──────────────────────────────────────────────
// Step Icon
// ──────────────────────────────────────────────

function StepIcon({ status }: { status: InstallStep['status'] }) {
  const size = 18;
  const base: any = {
    width: size, height: size, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 10, fontWeight: 700, flexShrink: 0,
  };

  if (status === 'done') return <div style={{ ...base, background: T.successBg, color: 'var(--success)' }}></div>;
  if (status === 'error') return <div style={{ ...base, background: T.dangerBg, color: 'var(--danger)' }}></div>;
  if (status === 'skipped') return <div style={{ ...base, background: T.borderHover, color: 'var(--text-muted)' }}>—</div>;
  if (status === 'running') return (
    <div style={{ ...base, border: '2px solid var(--primary)', background: 'transparent' }}>
      <div style={{
        width: 8, height: 8, border: '2px solid transparent',
        borderTopColor: 'var(--primary)', borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
    </div>
  );
  return <div style={{ ...base, background: T.borderHover, color: 'var(--text-muted)' }}>○</div>;
}

// ──────────────────────────────────────────────
// Styles (compact buttons)
// ──────────────────────────────────────────────

const btnPrimaryStyle: any = {
  padding: '6px 16px', background: 'linear-gradient(135deg, var(--secondary), var(--secondary))',
  color: T.text, border: 'none', borderRadius: 8,
  cursor: 'pointer', fontSize: 12, fontWeight: 600,
};

const btnSecondaryStyle: any = {
  padding: '6px 16px', background: T.border,
  color: 'var(--text-dim)', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
};

const btnDangerStyle: any = {
  padding: '6px 16px', background: 'rgba(248,81,73,0.1)',
  color: 'var(--danger)', border: '1px solid rgba(248,81,73,0.2)',
  borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
};

const btnSuccessStyle: any = {
  padding: '6px 16px', background: T.successBg,
  color: 'var(--success)', border: '1px solid rgba(46,204,113,0.2)',
  borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
};
